import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';

type ModalProps = {
  children?: React.ReactNode;
  show?: boolean;
  maxWidth?: 'sm' | 'md' | 'lg' | 'xl' | '2xl';
  closeable?: boolean;
  onClose?: () => void;
  closeOnOverlayClick?: boolean;
  zIndexClass?: string;
  panelClassName?: string;
};

// Lightweight modal without HeadlessUI to avoid rare "blank panel" sticking
// that some browsers produced after XHR/partial Inertia reloads.
export default function Modal({
  children,
  show = false,
  maxWidth = '2xl',
  closeable = true,
  onClose = () => {},
  closeOnOverlayClick = true,
  zIndexClass = 'z-50',
  panelClassName = '',
}: ModalProps) {
  const close = useCallback(() => { if (closeable) onClose(); }, [closeable, onClose]);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const hasRenderableChildren = useMemo(() => {
    const arr = React.Children.toArray(children);
    return arr.some((c) => {
      if (c === null || c === undefined || c === false || c === true) return false;
      if (typeof c === 'string') return c.trim().length > 0;
      return true;
    });
  }, [children]);

  useEffect(() => {
    if (!show) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') close(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [show, close]);

  // Extra guard: if for some reason the modal renders without visible content,
  // auto-close it to avoid the blank white block sticking around.
  useEffect(() => {
    if (!show) return;
    const panel = panelRef.current;
    if (!panel) return;
    const hasContent = () => {
      const text = (panel.innerText || '').trim();
      const hasUi = panel.querySelector('button,input,select,textarea,a,[role="button"],img,svg,video,canvas,[data-lp-modal-keep]');
      return text.length > 3 || !!hasUi;
    };
    const sweep = () => { if (!hasContent()) close(); };
    const timers = [40, 140, 320].map((ms) => setTimeout(sweep, ms));
    const mo = new MutationObserver(() => sweep());
    mo.observe(panel, { childList: true, subtree: true });
    return () => { timers.forEach(clearTimeout); mo.disconnect(); };
  }, [show, close]);

  if (!show) return null;
  // Safety: if for какой-то причине содержимое диалога не передано — вовсе не рендерим оболочку,
  // чтобы не было «пустого белого блока».
  if (!children || !hasRenderableChildren) {
    try { console.info('[Modal] skip render (empty children)'); } catch {}
    return null;
  }

  const maxWidthClass = {
    sm: 'sm:max-w-sm',
    md: 'sm:max-w-md',
    lg: 'sm:max-w-lg',
    xl: 'sm:max-w-xl',
    '2xl': 'sm:max-w-2xl',
  }[maxWidth];

  const node = (
    <div
      id="modal"
      role="dialog"
      aria-modal="true"
      className={`fixed inset-0 ${zIndexClass} pointer-events-auto flex items-center justify-center px-4 py-6 sm:px-0`}
      onMouseDown={() => { /* container */ }}
    >
      {/* Clickable backdrop: closes on outside click when enabled */}
      <div
        className="lp-modal-backdrop absolute inset-0 pointer-events-auto"
        aria-hidden="true"
        onClick={() => { if (closeOnOverlayClick) close(); }}
      />
      <div
        ref={panelRef}
        data-lp-modal
        className={`lp-modal-panel relative z-10 mb-6 overflow-hidden rounded-[1.25rem] border pointer-events-auto sm:mx-auto sm:w-full ${maxWidthClass} ${panelClassName}`}
      >
        {children}
      </div>
    </div>
  );

  // Render at the end of body to mimic portal behavior
  const container = typeof document !== 'undefined' ? document.body : undefined;
  return container ? createPortal(node, container) : node;
}
